import { QueryKey } from "@tanstack/react-query";
import { z } from "zod";

import { NetworkingLayerConfig } from "../../config";
import {
  createPollingRequester,
  createUsePollingHook,
  PollingDetermineResultCreator,
  PollingFailedError,
  PollingMaxAttemptsExceededError,
  PollingResult,
  RetryResult,
} from "../../polling-helper";
import { CallerCombinedVariables, QueryDefinitionOptions } from "../types";

export interface QueryPollingDefinitionOptions<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  TransformedResult extends object,
  DoneResult,
  PendingResult,
  FailedResult,
> {
  onSuccess?: (
    data: DoneResult,
    variables: CallerCombinedVariables<Path, ParamsSchema>
  ) => void | Promise<void>;

  onPollingPendingResult?: (
    result: PendingResult,
    variables: CallerCombinedVariables<Path, ParamsSchema>
  ) => void;

  onError?: (
    error:
      | Error
      | PollingFailedError<FailedResult>
      | PollingMaxAttemptsExceededError<ResponseSchema>,
    variables: CallerCombinedVariables<Path, ParamsSchema>
  ) => void;

  /** @default 100: number of attempts before giving up */
  maxPollingAttempts?: number;
  /** @default 1000: which is 1s between attempts */
  pollingInterval?: number;

  determinePollingStatus: (
    result: TransformedResult,
    resultCreator: PollingDetermineResultCreator
  ) => PollingResult<DoneResult, PendingResult, FailedResult>;

  determineRetry?: (err: unknown) => RetryResult;

  swallowAbortErrors?: boolean;
}

export function createQueryPollingMixin<
  TQueryKey extends QueryKey,
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  TransformedResult extends object,
>(
  config: NetworkingLayerConfig,
  {
    onSuccess: _,
    onError: __,
    ...originalDefinitionOptions
  }: QueryDefinitionOptions<TQueryKey, Path, ParamsSchema, ResponseSchema, TransformedResult>
) {
  return {
    withPolling<DoneResult, PendingResult, FailedResult>(
      pollingOptions: QueryPollingDefinitionOptions<
        Path,
        ParamsSchema,
        ResponseSchema,
        TransformedResult,
        DoneResult,
        PendingResult,
        FailedResult
      >
    ) {
      return {
        usePollingQuery: createUsePollingHook(config, {
          ...originalDefinitionOptions,
          ...pollingOptions,
        }),
        beginPolling: createPollingRequester(config, {
          ...originalDefinitionOptions,
          ...pollingOptions,
        }),
      };
    },
  };
}
